import React from 'react';
import { Pin, PinOff } from 'lucide-react';
import { useVisibleSections } from 'app/registry/sectionRegistry';
import { useVisibleSpineActions, type SpineActionDef } from 'app/registry/spineRegistry';
import { useConfigActions, useConfigInputs } from 'shared/state/configContexts';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';
import { useAllSectionStatus } from 'shared/state/sectionStatusContext';
import { useLayout } from './LayoutProvider';
import { useScrollSpy } from './useScrollSpy';
import { NavItem } from './NavItem';

const CURRENT_PHASE = 6;

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="flex flex-col items-stretch gap-1 px-2 py-2 max-[620px]:flex-row max-[620px]:items-center"
      role="group"
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function Spine() {
  const sections = useVisibleSections();
  const actions = useVisibleSpineActions();
  const layout = useLayout();
  const configActions = useConfigActions();
  const { config } = useConfigInputs();
  const quote = useQuoteActionsCtx();
  const statuses = useAllSectionStatus();
  useScrollSpy(sections.map((s) => s.id));

  const api = { layout, actions: configActions, persistence: { resetConfig: quote.resetConfig }, quote, config };
  const renderAction = (a: SpineActionDef) => {
    const wired = a.availableFrom <= CURRENT_PHASE;
    const title = wired ? a.label : `${a.label} — available in Phase ${a.availableFrom}`;
    // spec §3 ("Spine › Views: System Slide, Rack View (aria-pressed
    // overlays)"): an item that opens an overlay shows whether ITS overlay is the
    // one currently open, whichever group it sits in. NavItem already publishes
    // that state as `aria-current` — the same attribute the Configure/Commercial
    // sections use — so this reuses it rather than adding a second attribute
    // meaning the same thing. Indicator only: onSelect still just opens, so
    // clicking an open view re-opens it rather than closing it. Every other
    // action, New System and New Expansion included, is a one-shot with no state.
    const active = a.overlay ? layout.overlay === a.id : undefined;
    return (
      <NavItem key={a.id} label={a.label} icon={a.icon} disabled={!wired} active={active}
        title={title}
        onSelect={() => a.onSelect(api)} />
    );
  };

  const renderSection = (s: (typeof sections)[number]) => (
    <NavItem key={s.id} label={s.label} icon={s.icon} active={layout.activeSection === s.id}
      onSelect={() => layout.jumpTo(s.id)} dot={statuses[s.id] ?? 'none'} />
  );

  const configurationActions: SpineActionDef[] = actions.filter((a) => a.group === 'configuration');
  const viewActions: SpineActionDef[] = actions.filter((a) => a.group === 'views');

  return (
    <div className="flex h-full flex-col max-[620px]:flex-row max-[620px]:items-center max-[620px]:overflow-x-auto">
      <div className="max-[620px]:flex max-[620px]:flex-row max-[620px]:items-center">
        {/* The sidebar widens over the page on hover and on keyboard focus; the pin
            holds it open and moves the page over instead (Workbench). It leads the
            sidebar because the list is taller than a laptop screen for the fuller
            tiers, and a control at the bottom would start out of sight. There is no
            sidebar to pin on the bottom bar, so it is dropped there. */}
        <div className="max-[620px]:hidden">
          <Group label="Sidebar">
            <NavItem label="Keep sidebar open" icon={layout.spinePinned ? PinOff : Pin}
              pressed={layout.spinePinned} onSelect={layout.toggleSpinePin} />
          </Group>
          <div className="mx-3 border-t border-hairline/15 max-[620px]:hidden" />
        </div>
        <Group label="Configuration">{configurationActions.map(renderAction)}</Group>
        <div className="mx-3 border-t border-hairline/15 max-[620px]:hidden" />
        <Group label="Configure">
          {sections.filter((s) => s.group === 'configure').map(renderSection)}
        </Group>
        {sections.some((s) => s.group === 'commercial') && (
          <>
            <div className="mx-3 border-t border-hairline/15 max-[620px]:hidden" />
            <Group label="Commercial">
              {sections.filter((s) => s.group === 'commercial').map(renderSection)}
            </Group>
          </>
        )}
        <div className="mx-3 border-t border-hairline/15 max-[620px]:hidden" />
        <Group label="Views">{viewActions.map(renderAction)}</Group>
      </div>
    </div>
  );
}
